import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { kanal, stufeMindestens, type MeinProjekt } from '@beholder/shared';
import { api, modulPfad } from '../../core/api';
import { useLive } from '../../core/live';
import { WEB_MODULE } from '../../core/modul';
import { Eingabe, Knopf, datum, useAktion, useDaten } from '../../core/ui';

/** So viel braucht die Mindmap von einer Aufgabe (Modul „Aufgaben“, Route ?knoten=<id>). */
interface AufgabeKurz {
  id: string;
  titel: string;
  status: string;
  faellig: string | null;
  vorschlag?: boolean;
}

const STATUS: Record<string, string> = { offen: 'Offen', in_arbeit: 'In Arbeit', wartet: 'Wartet', erledigt: 'Erledigt' };

/** Darf die Seitenleiste die Aufgaben eines Punkts zeigen? (Modul vorhanden und Leserecht im Bereich Aufgaben) */
export function aufgabenSichtbar(projekt: MeinProjekt): boolean {
  return WEB_MODULE.some((m) => m.id === 'aufgaben') && stufeMindestens(projekt.rechte.aufgaben, 'lesen');
}

/** Aufgaben, die an diesem Mindmap-Punkt hängen – live, mit schnellem Anlegen (bei Schreibrecht in „Aufgaben“). */
export function PunktAufgaben({ projekt, knotenId }: { projekt: MeinProjekt; knotenId: string }) {
  const pfad = `${modulPfad(projekt.id, 'aufgaben')}?knoten=${encodeURIComponent(knotenId)}`;
  const { daten, neuLaden, fehler } = useDaten(() => api.get<AufgabeKurz[]>(pfad), [pfad]);
  useLive(kanal(projekt.id, 'aufgaben'), () => void neuLaden());
  const [titel, setTitel] = useState('');
  const a = useAktion();
  const darfAnlegen = projekt.rechte.aufgaben === 'schreiben' && !projekt.archiviert;

  async function anlegen(e: FormEvent) {
    e.preventDefault();
    const t = titel.trim();
    if (!t) return;
    const ok = await a.ausfuehren(() => api.post(modulPfad(projekt.id, 'aufgaben'), { titel: t, mindmapKnotenId: knotenId }));
    if (ok !== undefined) {
      setTitel('');
      await neuLaden();
    }
  }

  const offen = (daten ?? []).filter((x) => x.status !== 'erledigt');
  const erledigt = (daten ?? []).length - offen.length;

  return (
    <>
      {(fehler || a.fehler) && <p className="leise klein">{fehler || a.fehler}</p>}
      {daten?.length === 0 && <p className="leise klein">Keine Aufgaben an diesem Punkt.</p>}
      <ul className="mm-aufgaben">
        {(daten ?? [])
          .slice()
          .sort((x, y) => Number(x.status === 'erledigt') - Number(y.status === 'erledigt'))
          .map((x) => (
            <li key={x.id} className={x.status === 'erledigt' ? 'mm-aufgabe-erledigt' : ''}>
              <Link to={`/p/${projekt.id}/aufgaben/${x.id}`}>{x.titel}</Link>
              <span className="leise klein">
                {x.vorschlag ? 'Vorschlag · ' : ''}
                {STATUS[x.status] ?? x.status}
                {x.faellig ? ` · fällig ${datum(x.faellig)}` : ''}
              </span>
            </li>
          ))}
      </ul>
      {daten && daten.length > 0 && (
        <p className="leise klein">
          {offen.length} offen{erledigt ? `, ${erledigt} erledigt` : ''}
        </p>
      )}
      {darfAnlegen && (
        <form className="mm-aufgabe-neu" onSubmit={anlegen}>
          <Eingabe value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="Neue Aufgabe zu diesem Punkt …" maxLength={300} />
          <Knopf klein type="submit" disabled={!titel.trim() || a.laeuft}>
            Anlegen
          </Knopf>
        </form>
      )}
    </>
  );
}
